/**
 * Booth Selection Rules Service
 * Handles business logic for booth purchase constraints and requirements
 */

// Rule Types
export const RULE_TYPES = {
  MANDATORY_ADDITION: 'MANDATORY_ADDITION',
  MANDATORY_GROUP: 'MANDATORY_GROUP',
  PROXIMITY_CONSTRAINT: 'PROXIMITY_CONSTRAINT',
  BUSINESS_LOGIC: 'BUSINESS_LOGIC'
};

// Rule Triggers
export const RULE_TRIGGERS = {
  SELECTION: 'SELECTION',
  CART: 'CART',
  CHECKOUT: 'CHECKOUT'
};

// Default booth rules configuration
export const BOOTH_RULES_CONFIG = {
  // Rule 1: Corner booth adjacency requirement
  CORNER_ADJACENCY: {
    id: 'corner-adjacency',
    name: 'Corner Booth Adjacent Purchase Rule',
    description: 'Corner booths require purchasing at least one adjacent booth',
    type: RULE_TYPES.MANDATORY_ADDITION,
    trigger: RULE_TRIGGERS.SELECTION,
    priority: 1,
    enabled: false,
    autoSuggest: true,
    validator: (selectedBooths, gridData, config) => {
      return validateCornerAdjacencyRule(selectedBooths, gridData, config);
    }
  },

  // Rule 2: Adjacent corner booths require additional non-corner booths
  MULTIPLE_CORNERS: {
    id: 'multiple-corners',
    name: 'Adjacent Corner Booth Requirements',
    description: 'When selecting any two adjacent corner booths, you must also purchase 2 additional adjacent non-corner booths (total of 4 booths)',
    type: RULE_TYPES.MANDATORY_ADDITION,
    trigger: RULE_TRIGGERS.SELECTION,
    priority: 2,
    enabled: true,
    autoSuggest: true,
    validator: (selectedBooths, gridData, config) => {
      return validateMultipleCornersRule(selectedBooths, gridData, config);
    }
  },

  // Rule 3: Group purchase requirements
  GROUP_PURCHASE: {
    id: 'group-purchase',
    name: 'Group Purchase Requirement',
    description: 'Certain booth groups must be purchased together',
    type: RULE_TYPES.MANDATORY_GROUP,
    trigger: RULE_TRIGGERS.SELECTION,
    priority: 3,
    enabled: true,
    autoSuggest: false,
    groups: [
      // Example groups - these would be configured per event
      {
        id: 'premium-block-1',
        name: 'Premium Block A',
        booths: ['R1C1', 'R1C2', 'R1C3'],
        description: 'Premium entrance block must be purchased entirely'
      },
      {
        id: 'sponsor-row',
        name: 'Sponsor Row',
        booths: ['R2C1', 'R2C2', 'R2C3', 'R2C4'],
        description: 'Sponsor row requires minimum 2 consecutive booths'
      }
    ],
    validator: (selectedBooths, gridData, config) => {
      return validateGroupPurchaseRule(selectedBooths, gridData, config);
    }
  },

  // Rule 4: Minimum purchase requirements
  MINIMUM_PURCHASE: {
    id: 'minimum-purchase',
    name: 'Minimum Purchase Requirement',
    description: 'Minimum number of booths required per vendor',
    type: RULE_TYPES.BUSINESS_LOGIC,
    trigger: RULE_TRIGGERS.CART,
    priority: 4,
    enabled: false, // Disabled by default
    minBooths: 1,
    validator: (selectedBooths, gridData, config) => {
      return validateMinimumPurchaseRule(selectedBooths, gridData, config);
    }
  },

  // Rule 5: Maximum daily booth purchase limit
  MAX_DAILY_PURCHASE: {
    id: 'max-daily-purchase',
    name: 'Maximum Daily Booth Purchase Limit',
    description: 'Maximum number of booths that can be purchased per day',
    type: RULE_TYPES.BUSINESS_LOGIC,
    trigger: RULE_TRIGGERS.CART,
    priority: 5,
    enabled: true,
    maxBoothsPerDay: 6, // Maximum booths allowed per day
    timeZone: 'America/New_York', // Configure based on event timezone
    validator: (selectedBooths, gridData, config) => {
      return validateMaxDailyPurchaseRule(selectedBooths, gridData, config);
    }
  }
};

// Normalize a booth identifier from various formats to the grid's 4-digit id (e.g., 0101)
const normalizeBoothId = (val, gridData = []) => {
  if (!val) return val;
  const s = String(val).toUpperCase().trim();
  const pad2 = (n) => n.toString().padStart(2, '0');
  const toGridId = (r, c) => `${pad2(parseInt(r,10))}${pad2(parseInt(c,10))}`;

  // Already a 4-digit id
  if (/^\d{4}$/.test(s)) return s;

  // Try direct matches on existing data
  const direct = gridData.find(b => String(b.id).toUpperCase() === s || String(b.number).toUpperCase() === s);
  if (direct) return direct.id;

  // CELL-r-c
  let m = s.match(/^CELL-(\d+)-(\d+)$/i);
  if (m) return toGridId(m[1], m[2]);

  // RrCc
  m = s.match(/^R(\d+)C(\d+)$/i);
  if (m) return toGridId(m[1], m[2]);

  // r-c
  m = s.match(/^(\d+)-(\d+)$/);
  if (m) return toGridId(m[1], m[2]);

  // AA01 style
  if (/^[A-Z]{1,2}\d{2}$/.test(s)) {
    const booth = gridData.find(b => String(b.number).toUpperCase() === s);
    if (booth) return booth.id;
    const letters = s.replace(/\d+/g, '');
    const digits = s.replace(/\D+/g, '');
    let row = 0;
    for (let i = 0; i < letters.length; i++) {
      row = row * 26 + (letters.charCodeAt(i) - 64);
    }
    return toGridId(row, parseInt(digits, 10));
  }

  // 3-digit legacy like 101 -> row*100+col
  if (/^\d{3}$/.test(s)) {
    const n = parseInt(s, 10);
    const row = Math.floor(n / 100);
    const col = n % 100;
    return toGridId(row, col);
  }

  return s;
};

/**
 * Get adjacent booth positions for a given booth
 */
export const getAdjacentPositions = (booth, gridData) => {
  const adjacent = [];
  const directions = [
    { row: -1, col: 0 },  // Above
    { row: 1, col: 0 },   // Below
    { row: 0, col: -1 },  // Left
    { row: 0, col: 1 }    // Right
  ];

  directions.forEach(dir => {
    const newRow = booth.row + dir.row;
    const newCol = booth.col + dir.col;
    const adjacentBooth = gridData.find(b => b.row === newRow && b.col === newCol);
    // For validation, treat adjacency purely by position regardless of availability.
    // Availability filtering should be applied by callers when generating suggestions.
    if (adjacentBooth) {
      adjacent.push(adjacentBooth);
    }
  });

  return adjacent;
};

/**
 * Check if a booth is a corner booth
 */
export const isCornerBooth = (booth) => {
  return booth.corner === 'Y';
};

/**
 * Check if two booths are adjacent to each other
 */
export const areBoothsAdjacent = (booth1, booth2) => {
  if (!booth1 || !booth2) return false;
  
  const rowDiff = Math.abs(booth1.row - booth2.row);
  const colDiff = Math.abs(booth1.col - booth2.col);
  
  // Adjacent means exactly one step away in row OR column (not diagonal)
  return (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);
};

/**
 * Find pairs of adjacent corner booths
 */
export const findAdjacentCornerPairs = (cornerBooths) => {
  const adjacentPairs = [];
  
  for (let i = 0; i < cornerBooths.length; i++) {
    for (let j = i + 1; j < cornerBooths.length; j++) {
      if (areBoothsAdjacent(cornerBooths[i], cornerBooths[j])) {
        adjacentPairs.push([cornerBooths[i], cornerBooths[j]]);
      }
    }
  }
  
  return adjacentPairs;
};

/**
 * Validate corner booth adjacency rule
 */
const validateCornerAdjacencyRule = (selectedBooths, gridData, config) => {
  const violations = [];
  const suggestions = [];
  const requiredBooths = [];

  selectedBooths.forEach(boothId => {
    const booth = gridData.find(b => b.id === boothId);
    if (!booth || !isCornerBooth(booth)) return;
    // If this booth belongs to a predefined group, corner-adjacency rules do not apply
    if (booth.groupid) return;

    const adjacentBooths = getAdjacentPositions(booth, gridData);
    const selectedAdjacentBooths = adjacentBooths.filter(adj => 
      selectedBooths.includes(adj.id)
    );

    if (selectedAdjacentBooths.length === 0) {
      violations.push({
        ruleId: config.id,
        type: 'MISSING_ADJACENT',
        booth: booth,
        message: `Corner booth ${booth.number} requires at least one adjacent booth`,
        severity: 'ERROR'
      });

      // Add suggestions for adjacent booths
      adjacentBooths.forEach(adj => {
        if (adj.status === 'Available') {
          suggestions.push({
            boothId: adj.id,
            reason: `Required adjacent booth for corner booth ${booth.number}`,
            priority: 'HIGH'
          });
          requiredBooths.push(adj.id);
        }
      });
    }
  });

  return {
    valid: violations.length === 0,
    violations,
    suggestions,
    requiredBooths
  };
};

/**
 * Validate adjacent corner booth requirements rule
 * When any 2 adjacent corner booths are selected, 2 additional adjacent non-corner booths must be purchased (total 4 booths)
 */
const validateMultipleCornersRule = (selectedBooths, gridData, config) => {
  const cornerBooths = selectedBooths
    .map(id => gridData.find(b => b.id === id))
    .filter(booth => booth && isCornerBooth(booth) && !booth.groupid);

  if (cornerBooths.length < 2) {
    return { valid: true, violations: [], suggestions: [], requiredBooths: [] };
  }

  // Find pairs of adjacent corner booths
  const adjacentCornerPairs = findAdjacentCornerPairs(cornerBooths);
  
  // If no corner booths are adjacent to each other, rule doesn't apply
  if (adjacentCornerPairs.length === 0) {
    return { valid: true, violations: [], suggestions: [], requiredBooths: [] };
  }

  const violations = [];
  const suggestions = [];
  const requiredBooths = [];

  // For each pair of adjacent corner booths, we need 2 additional adjacent non-corner booths
  adjacentCornerPairs.forEach(([cornerBooth1, cornerBooth2]) => {
    // Get all adjacent positions for both corner booths
    const adjacentToBooth1 = getAdjacentPositions(cornerBooth1, gridData);
    const adjacentToBooth2 = getAdjacentPositions(cornerBooth2, gridData);
    
    // Combine and remove duplicates
    const allAdjacentBooths = [...adjacentToBooth1, ...adjacentToBooth2];
    const uniqueAdjacentBooths = allAdjacentBooths.filter((booth, index, self) => 
      index === self.findIndex(b => b.id === booth.id)
    );

    // Consider non-corner booths adjacent to either corner booth
    const adjacentNonCornerBooths = uniqueAdjacentBooths.filter(booth => !isCornerBooth(booth));

    // Determine candidate adjacent non-corner pairs that form a contiguous 2-booth block
    // For horizontal corner pairs (same row) the candidates are the two booths above or the two booths below
    // For vertical corner pairs (same column) the candidates are the two booths to the left or the two booths to the right
    const candidatePairs = [];
    if (cornerBooth1.row === cornerBooth2.row) {
      // horizontal pair
      const rUp = cornerBooth1.row - 1;
      const rDown = cornerBooth1.row + 1;
      const c1 = cornerBooth1.col;
      const c2 = cornerBooth2.col;
      const up1 = gridData.find(b => b.row === rUp && b.col === c1);
      const up2 = gridData.find(b => b.row === rUp && b.col === c2);
      const down1 = gridData.find(b => b.row === rDown && b.col === c1);
      const down2 = gridData.find(b => b.row === rDown && b.col === c2);
      if (up1 && up2) candidatePairs.push([up1, up2]);
      if (down1 && down2) candidatePairs.push([down1, down2]);
    } else if (cornerBooth1.col === cornerBooth2.col) {
      // vertical pair
      const cLeft = cornerBooth1.col - 1;
      const cRight = cornerBooth1.col + 1;
      const r1 = cornerBooth1.row;
      const r2 = cornerBooth2.row;
      const left1 = gridData.find(b => b.row === r1 && b.col === cLeft);
      const left2 = gridData.find(b => b.row === r2 && b.col === cLeft);
      const right1 = gridData.find(b => b.row === r1 && b.col === cRight);
      const right2 = gridData.find(b => b.row === r2 && b.col === cRight);
      if (left1 && left2) candidatePairs.push([left1, left2]);
      if (right1 && right2) candidatePairs.push([right1, right2]);
    }

    // If no candidate contiguous pairs found, skip enforcement for this corner pair
    if (candidatePairs.length === 0) return;

    // Find at least one candidate pair where both booths are available or selected
    let validPair = null;
    for (const pair of candidatePairs) {
      const bothOk = pair.every(booth => (booth.status === 'Available' || selectedBooths.includes(booth.id)));
      if (bothOk) {
        validPair = pair;
        break;
      }
    }

    // If no contiguous pair is fully available/selected, skip enforcement per requirement
    if (!validPair) return;

    // Now restrict available/selected non-corner lists to the chosen validPair
    const availableNonCornerBooths = validPair.filter(booth => booth.status === 'Available' && !selectedBooths.includes(booth.id));
    const selectedNonCornerBooths = validPair.filter(booth => selectedBooths.includes(booth.id));

    const requiredAdditionalBooths = 2;
    const currentAdditionalBooths = selectedNonCornerBooths.length;
    const stillNeeded = requiredAdditionalBooths - currentAdditionalBooths;

    if (stillNeeded > 0) {
      violations.push({
        ruleId: config.id,
        type: 'ADJACENT_CORNERS_REQUIREMENT',
        cornerBooths: [cornerBooth1, cornerBooth2],
        message: `Adjacent corner booths ${cornerBooth1.number} and ${cornerBooth2.number} require ${requiredAdditionalBooths} additional adjacent non-corner booths. Currently have ${currentAdditionalBooths}, need ${stillNeeded} more.`,
        severity: 'ERROR'
      });

      // Suggest available non-corner booths (limit to how many we still need)
      const boothsToSuggest = availableNonCornerBooths.slice(0, stillNeeded);
      boothsToSuggest.forEach(booth => {
        suggestions.push({
          boothId: booth.id,
          reason: `Required additional booth for adjacent corner booths ${cornerBooth1.number}-${cornerBooth2.number}`,
          priority: 'HIGH'
        });
        requiredBooths.push(booth.id);
      });

      // If we don't have enough available adjacent booths, show a warning
      if (availableNonCornerBooths.length < stillNeeded) {
        violations.push({
          ruleId: config.id,
          type: 'INSUFFICIENT_ADJACENT_BOOTHS',
          cornerBooths: [cornerBooth1, cornerBooth2],
          message: `Warning: Only ${availableNonCornerBooths.length} adjacent non-corner booths available, but ${stillNeeded} required for corner booths ${cornerBooth1.number}-${cornerBooth2.number}`,
          severity: 'WARNING'
        });
      }
    }
  });

  return {
    valid: violations.length === 0 || violations.every(v => v.severity === 'WARNING'),
    violations,
    suggestions,
    requiredBooths
  };
};

/**
 * Validate group purchase rule
 */
const validateGroupPurchaseRule = (selectedBooths, gridData, config) => {
  const violations = [];
  const suggestions = [];
  const requiredBooths = [];

  (config.groups || []).forEach(group => {
    // Normalize and validate booth ids against grid
    const normalizedIds = (group.booths || []).map(id => normalizeBoothId(id, gridData));
    const validGroupBooths = normalizedIds.filter(id => gridData.some(b => b.id === id));

    const selectedFromGroup = validGroupBooths.filter(boothId => selectedBooths.includes(boothId));

    // All-or-none: if any booth from the group is selected, all group booths must be selected
    if (selectedFromGroup.length > 0 && selectedFromGroup.length < validGroupBooths.length) {
      const remainingBooths = validGroupBooths.filter(boothId => !selectedBooths.includes(boothId));

      violations.push({
        ruleId: config.id,
        type: 'INCOMPLETE_GROUP',
        group: group,
        selectedCount: selectedFromGroup.length,
        requiredCount: validGroupBooths.length,
        message: `${group.name}: You must purchase all booths in this group. Selected ${selectedFromGroup.length} of ${validGroupBooths.length}.`,
        severity: 'ERROR'
      });

      // Suggest remaining booths in the group (only those available)
      remainingBooths.forEach(boothId => {
        const booth = gridData.find(b => b.id === boothId);
        if (booth && booth.status === 'Available') {
          suggestions.push({
            boothId: boothId,
            reason: `Required to complete group: ${group.name}`,
            priority: 'HIGH'
          });
          requiredBooths.push(boothId);
        }
      });
    } else if (selectedFromGroup.length > 0 && validGroupBooths.length === 0) {
      // Selected from supposed group but none of the group booths are valid; flag configuration issue
      violations.push({
        ruleId: config.id,
        type: 'INVALID_GROUP_BOOTHS',
        group: group,
        selectedCount: selectedFromGroup.length,
        requiredCount: 0,
        message: `${group.name}: No valid booth IDs found in this group for the current layout`,
        severity: 'WARNING'
      });
    }
  });

  return {
    valid: violations.length === 0,
    violations,
    suggestions,
    requiredBooths
  };
};

/**
 * Validate minimum purchase rule
 */
const validateMinimumPurchaseRule = (selectedBooths, gridData, config) => {
  const violations = [];
  
  if (selectedBooths.length < config.minBooths) {
    violations.push({
      ruleId: config.id,
      type: 'MINIMUM_PURCHASE',
      selectedCount: selectedBooths.length,
      requiredCount: config.minBooths,
      message: `Minimum ${config.minBooths} booths required. Currently selected: ${selectedBooths.length}`,
      severity: 'WARNING'
    });
  }

  return {
    valid: violations.length === 0,
    violations,
    suggestions: [],
    requiredBooths: []
  };
};

/**
 * Validate maximum daily purchase rule
 */
const validateMaxDailyPurchaseRule = (selectedBooths, gridData, config) => {
  const violations = [];
  
  // Get current date in the specified timezone
  const now = new Date();
  const today = new Intl.DateTimeFormat('en-CA', { 
    timeZone: config.timeZone || 'UTC' 
  }).format(now);
  
  // Get today's purchase count from localStorage or session storage
  // In a real application, this would come from your backend/database
  const storageKey = `booth_purchases_${today}`;
  const todaysPurchases = JSON.parse(localStorage.getItem(storageKey) || '[]');
  
  // Calculate total booths for today (existing + current selection)
  const totalBoothsToday = todaysPurchases.length + selectedBooths.length;
  
  if (totalBoothsToday > config.maxBoothsPerDay) {
    const remaining = config.maxBoothsPerDay - todaysPurchases.length;
    violations.push({
      ruleId: config.id,
      type: 'MAX_DAILY_PURCHASE_EXCEEDED',
      selectedCount: selectedBooths.length,
      dailyLimit: config.maxBoothsPerDay,
      todaysPurchases: todaysPurchases.length,
      remainingToday: Math.max(totalBoothsToday, remaining),
      totalToday: totalBoothsToday,
      message: `Booth purchase limit exceeded: ${totalBoothsToday} Booked, Maximum of ${config.maxBoothsPerDay} booths allowed. `,
      severity: 'ERROR',
      date: today
    });
  }

  return {
    valid: violations.length === 0,
    violations,
    suggestions: [],
    requiredBooths: []
  };
};

/**
 * Main rule validation function
 */
export const validateBoothRules = (selectedBooths, gridData, rulesConfig = BOOTH_RULES_CONFIG) => {
  const results = {
    valid: true,
    violations: [],
    suggestions: [],
    requiredBooths: [],
    ruleResults: {}
  };

  // Get enabled rules sorted by priority
  const enabledRules = Object.values(rulesConfig)
    .filter(rule => rule.enabled)
    .sort((a, b) => a.priority - b.priority);

  // Run each rule validation
  enabledRules.forEach(rule => {
    const ruleResult = rule.validator(selectedBooths, gridData, rule);
    results.ruleResults[rule.id] = ruleResult;

    if (!ruleResult.valid) {
      results.valid = false;
      results.violations.push(...ruleResult.violations);
      results.suggestions.push(...ruleResult.suggestions);
      results.requiredBooths.push(...ruleResult.requiredBooths);
    }
  });

  // Remove duplicate required booths
  results.requiredBooths = [...new Set(results.requiredBooths)];

  return results;
};

/**
 * Get booth purchase suggestions based on current selection
 */
export const getBoothSuggestions = (selectedBooths, gridData, rulesConfig = BOOTH_RULES_CONFIG) => {
  const validation = validateBoothRules(selectedBooths, gridData, rulesConfig);
  
  return {
    hasRequiredBooths: validation.requiredBooths.length > 0,
    requiredBooths: validation.requiredBooths,
    suggestions: validation.suggestions,
    autoSuggestRules: Object.values(rulesConfig)
      .filter(rule => rule.enabled && rule.autoSuggest)
      .map(rule => ({
        id: rule.id,
        name: rule.name,
        description: rule.description
      }))
  };
};

/**
 * Apply auto-suggestions to selection
 */
export const applyAutoSuggestions = (currentSelection, suggestions, gridData) => {
  const newSelection = [...currentSelection];
  
  suggestions.forEach(suggestion => {
    if (!newSelection.includes(suggestion.boothId)) {
      const booth = gridData.find(b => b.id === suggestion.boothId);
      if (booth && booth.status === 'Available') {
        newSelection.push(suggestion.boothId);
      }
    }
  });

  return newSelection;
};

/**
 * Check if booth selection is ready for checkout
 */
export const isSelectionReadyForCheckout = (selectedBooths, gridData, rulesConfig = BOOTH_RULES_CONFIG) => {
  const validation = validateBoothRules(selectedBooths, gridData, rulesConfig);
  
  // Only check ERROR severity violations for checkout
  const errorViolations = validation.violations.filter(v => v.severity === 'ERROR');
  
  return {
    ready: errorViolations.length === 0,
    violations: errorViolations,
    warnings: validation.violations.filter(v => v.severity === 'WARNING')
  };
};

/**
 * Record booth purchases for daily limit tracking
 * Call this function when a purchase is completed
 */
export const recordDailyPurchase = (purchasedBooths, timeZone = 'UTC') => {
  const now = new Date();
  const today = new Intl.DateTimeFormat('en-CA', { 
    timeZone: timeZone 
  }).format(now);
  
  const storageKey = `booth_purchases_${today}`;
  const todaysPurchases = JSON.parse(localStorage.getItem(storageKey) || '[]');
  
  // Add new purchases with timestamp
  const newPurchases = purchasedBooths.map(boothId => ({
    boothId,
    timestamp: now.toISOString(),
    date: today
  }));
  
  const updatedPurchases = [...todaysPurchases, ...newPurchases];
  localStorage.setItem(storageKey, JSON.stringify(updatedPurchases));
  
  return {
    totalToday: updatedPurchases.length,
    newPurchases: newPurchases.length
  };
};

/**
 * Get today's booth purchase count for daily limit checking
 */
export const getTodaysPurchaseCount = (timeZone = 'UTC') => {
  const now = new Date();
  const today = new Intl.DateTimeFormat('en-CA', { 
    timeZone: timeZone 
  }).format(now);
  
  const storageKey = `booth_purchases_${today}`;
  const todaysPurchases = JSON.parse(localStorage.getItem(storageKey) || '[]');
  
  return {
    count: todaysPurchases.length,
    purchases: todaysPurchases,
    date: today
  };
};

/**
 * Check if current selection would exceed daily limit
 */
export const checkDailyLimit = (selectedBooths, maxBoothsPerDay, timeZone = 'UTC') => {
  const todaysData = getTodaysPurchaseCount(timeZone);
  const totalToday = todaysData.count + selectedBooths.length;
  
  return {
    withinLimit: totalToday <= maxBoothsPerDay,
    totalToday,
    dailyLimit: maxBoothsPerDay,
    remaining: Math.max(0, maxBoothsPerDay - todaysData.count),
    alreadyPurchased: todaysData.count
  };
};